// @ts-nocheck
/**
 * Add API Webhook — redesigned to obd3-gui pattern:
 * ScrollPage + PageHeader + Card sections. Business logic identical to
 * v1 (ApiBuilder-driven URL/body/headers, request-parameters multi-select
 * with per-row inline value edit, response-parameters picker, postman-style
 * body/headers panel with pretty/raw toggle, confirm-before-submit).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, ChevronDown, Loader2, Pencil, Save, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import { cn } from '@/lib/utils/cn';
import { getApiCall, postApiCall } from '@/service/httpCall';
import {
  API_APIWEBHOOK_CREATE,
  API_APIWEBHOOK_REQUEST_PARAMETERS_LIST,
} from '@/service/url';
import { ROUTES, FLASH_MESSAGE_ACTION_OBJECT, MODULE_ACL } from '@/utilities/constant';
import ApiBuilder from './components/ApiBuilder';
import CopyButton from './components/CopyButton';
import { isWebhookUrl, syntaxHighlight } from './components/apiBuilderUtils';
import { fetchAndSetFlashMessage } from '@/service/fetchFlashMessage';

const CreateApiWebhook = () => {
  const navigate = useNavigate();

  const [webhook_name, setWebhookName] = useState('');
  const [request_type, setRequestType] = useState('');
  const [url, setUrl] = useState('');
  const [body_text, setBodyText] = useState('');
  const [payload, setPayload] = useState('');
  const [header, setHeader] = useState('');

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alertMessage, setAlertMessage] = useState<any>(null);
  const [bodyViewMode, setBodyViewMode] = useState('pretty');
  const [bodyTab, setBodyTab] = useState('body');
  const [responseHeaders, setResponseHeaders] = useState<any>({});
  const [responseStatus, setResponseStatus] = useState<any>(null);
  const [responseParams, setResponseParams] = useState<any[]>([]);
  const [selectedParams, setSelectedParams] = useState<Set<any>>(new Set());
  const [parameterJson, setParameterJson] = useState('');

  const [requestParamOptions, setRequestParamOptions] = useState<any[]>([]);
  const [selectedRequestParams, setSelectedRequestParams] = useState<any[]>([]);
  const [paramDropdownOpen, setParamDropdownOpen] = useState(false);
  const paramDropdownRef = useRef<any>(null);
  const [paramCustomValues, setParamCustomValues] = useState<any>({});
  const [editingParamId, setEditingParamId] = useState<any>(null);
  const [editingParamValue, setEditingParamValue] = useState('');
  const [requestBodySnapshot] = useState('');

  useEffect(() => {
    const onDoc = (e: any) => {
      if (paramDropdownRef.current && !paramDropdownRef.current.contains(e.target)) {
        setParamDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const toggleRequestParam = (id: any) => {
    if (selectedRequestParams.includes(id)) {
      setSelectedRequestParams((prev) => prev.filter((v) => v !== id));
      return;
    }
    const newOpt = requestParamOptions.find((o) => o.value === id);
    const newValue = paramCustomValues[id] ?? newOpt?.parameter_value;
    const conflictId = selectedRequestParams.find((existingId) => {
      const existingOpt = requestParamOptions.find((o) => o.value === existingId);
      return (paramCustomValues[existingId] ?? existingOpt?.parameter_value) === newValue;
    });
    if (conflictId) {
      const conflictOpt = requestParamOptions.find((o) => o.value === conflictId);
      toast.error(`"${newValue}" is already used by parameter "${conflictOpt?.parameter_name}"`);
      return;
    }
    setSelectedRequestParams((prev) => [...prev, id]);
  };

  const startEditParam = (id: any) => {
    const opt = requestParamOptions.find((o) => o.value === id);
    setEditingParamId(id);
    setEditingParamValue(paramCustomValues[id] ?? opt?.parameter_value ?? '');
  };

  const saveEditParam = () => {
    if (!editingParamValue.trim()) {
      toast.error('Parameter value cannot be empty');
      return;
    }
    const conflictId = selectedRequestParams.find((id) => {
      if (id === editingParamId) return false;
      const opt = requestParamOptions.find((o) => o.value === id);
      return (paramCustomValues[id] ?? opt?.parameter_value) === editingParamValue;
    });
    if (conflictId) {
      const conflictOpt = requestParamOptions.find((o) => o.value === conflictId);
      toast.error(
        `"${editingParamValue}" is already used by parameter "${conflictOpt?.parameter_name}"`,
      );
      return;
    }
    setParamCustomValues((prev: any) => ({ ...prev, [editingParamId]: editingParamValue }));
    setEditingParamId(null);
    setEditingParamValue('');
  };

  const cancelEditParam = () => {
    setEditingParamId(null);
    setEditingParamValue('');
  };

  useEffect(() => {
    getApiCall(API_APIWEBHOOK_REQUEST_PARAMETERS_LIST)
      .then((data: any) => {
        const active = (data || []).filter((p: any) => p.status === 1 || p.status === '1');
        setRequestParamOptions(
          active.map((p: any) => ({
            label: p.parameter_name,
            value: p.id,
            parameter_name: p.parameter_name,
            parameter_value: p.parameter_value,
          })),
        );
      })
      .catch(() => toast.error('Failed to load request parameters'));
  }, []);

  useEffect(() => {
    fetchAndSetFlashMessage(
      (msg: any) =>
        msg.module_name === MODULE_ACL.API_WEBHOOK &&
        msg.action === FLASH_MESSAGE_ACTION_OBJECT.ADD &&
        msg.type === 'Flash Message',
      setAlertMessage,
    );
  }, []);

  const apiMethod = useMemo(() => request_type || 'GET', [request_type]);

  const fixedJson = useMemo(() => {
    if (selectedRequestParams.length === 0) return null;
    const result: any = {};
    selectedRequestParams.forEach((id) => {
      const found = requestParamOptions.find((opt) => opt.value === id);
      if (found) result[found.parameter_name] = paramCustomValues[id] ?? found.parameter_value;
    });
    return Object.keys(result).length > 0 ? JSON.stringify(result) : null;
  }, [selectedRequestParams, requestParamOptions, paramCustomValues]);

  const handleApiBuilderChange = (apiState: any) => {
    setUrl(apiState?.url || '');
    if (!apiState) {
      setHeader('');
      return;
    }
    if (apiState.headerMode === 'raw') {
      setHeader((apiState.rawHeaders || '').trim());
    } else {
      const headerObj = (apiState.headers || []).reduce((acc: any, h: any) => {
        const key = (h?.key || '').trim();
        if (!key) return acc;
        acc[key] = h?.value ?? '';
        return acc;
      }, {});
      setHeader(Object.keys(headerObj).length ? JSON.stringify(headerObj) : '');
    }
    if (['POST'].includes(request_type)) {
      if (apiState.bodyMode === 'raw') {
        setPayload(apiState.rawBody || '');
      } else {
        const bodyObj = (apiState.body || []).reduce((acc: any, { key, value }: any) => {
          if (!(key || '').trim()) return acc;
          acc[key.trim()] = value;
          return acc;
        }, {});
        setPayload(Object.keys(bodyObj).length ? JSON.stringify(bodyObj, null, 2) : '');
      }
    }
  };

  const handleUseUrl = (u: string) => setUrl(u || '');
  const handleViewModeChange = (mode: string) => setBodyViewMode(mode);
  const getLineCount = (val: string) => {
    const lines = (val || '').split('\n').length;
    return Array.from({ length: Math.max(lines, 1) }, (_, i) => i + 1);
  };

  const extractParams = (bodyString: string) => {
    const toSample = (v: any) =>
      v === null ? 'null' : typeof v === 'object' ? JSON.stringify(v) : String(v);
    try {
      const parsed = JSON.parse(bodyString);
      if (Array.isArray(parsed)) {
        const allParams: any[] = [];
        parsed.forEach((item: any, index: number) => {
          if (item && typeof item === 'object' && !Array.isArray(item)) {
            Object.entries(item).forEach(([k, value]) => {
              allParams.push({
                key: `[${index}].${k}`,
                displayKey: k,
                groupKey: `[${index}]`,
                sampleValue: toSample(value),
              });
            });
          }
        });
        return allParams;
      }
      if (parsed && typeof parsed === 'object') {
        const allParams: any[] = [];
        Object.entries(parsed).forEach(([parentKey, parentValue]: any) => {
          if (Array.isArray(parentValue)) {
            parentValue.forEach((item: any, index: number) => {
              if (item && typeof item === 'object' && !Array.isArray(item)) {
                Object.entries(item).forEach(([k, value]) => {
                  allParams.push({
                    key: `${parentKey}.[${index}].${k}`,
                    displayKey: k,
                    groupKey: `${parentKey}.[${index}]`,
                    sampleValue: toSample(value),
                  });
                });
              }
            });
          } else {
            allParams.push({
              key: parentKey,
              displayKey: parentKey,
              sampleValue: toSample(parentValue),
            });
          }
        });
        return allParams;
      }
    } catch {}
    return [];
  };

  const handleBuilderResponse = (bodyString: string, headers?: any, statusInfo?: any) => {
    if (statusInfo) {
      setBodyText(bodyString || '');
      if (headers && Object.keys(headers).length) setResponseHeaders(headers);
      setResponseStatus(statusInfo);
      setResponseParams(extractParams(bodyString || ''));
      setSelectedParams(new Set());
      setParameterJson('');
      setBodyTab('body');
    } else {
      if (request_type === 'GET') setBodyText(bodyString || '');
      else setPayload(bodyString || '');
    }
  };

  const handleParamToggle = (key: any) => {
    setSelectedParams((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const handleSelectAll = () => setSelectedParams(new Set(responseParams.map((p) => p.key)));
  const handleDeselectAll = () => {
    setSelectedParams(new Set());
    setParameterJson('');
  };
  const handleSaveParams = () => {
    const result: any = {};
    responseParams
      .filter((p) => selectedParams.has(p.key))
      .forEach((p) => (result[p.key] = `{${p.key}}`));
    setParameterJson(JSON.stringify(result, null, 2));
  };

  const handleBuilderClear = () => {
    setBodyText('');
    setPayload('');
    setResponseHeaders({});
    setResponseStatus(null);
    setBodyTab('body');
    setBodyViewMode('pretty');
    setResponseParams([]);
    setSelectedParams(new Set());
    setParameterJson('');
  };

  const statusToneClass = (s: number) => {
    if (!s) return 'bg-muted text-muted-foreground border-border';
    if (s >= 200 && s < 300)
      return 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30';
    if (s >= 300 && s < 400)
      return 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30';
    if (s >= 400) return 'bg-destructive/15 text-destructive border-destructive/30';
    return 'bg-muted text-muted-foreground border-border';
  };

  const validate = () => {
    const name = webhook_name.trim();
    const u = url.trim();
    const type = request_type;
    if (!name || !u || !type) {
      toast.error('Please fill all required fields.');
      return false;
    }
    if (!isWebhookUrl(u)) {
      toast.error('Please enter a valid Webhook URL (must start with http:// or https://).');
      return false;
    }
    if (header && header.trim()) {
      try {
        JSON.parse(header);
      } catch {
        toast.error('Headers must be valid JSON (use Api Builder headers section).');
        return false;
      }
    }
    if (['POST'].includes(type) && !payload.trim()) {
      toast.error('Payload is required for this request type.');
      return false;
    }
    return true;
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    if (!validate()) return;
    setShowConfirmModal(true);
  };

  const submitWebhook = async () => {
    setShowConfirmModal(false);
    setSaving(true);
    const body = {
      webhook_name: webhook_name.trim(),
      url: url.trim(),
      request_type,
      body_text: null,
      api_text: request_type === 'GET' ? body_text.trim() : null,
      payload: ['POST'].includes(request_type)
        ? payload && payload.trim()
          ? payload.trim()
          : null
        : null,
      header: header && header.trim() ? header.trim() : null,
      response_parameters: parameterJson && parameterJson.trim() ? parameterJson.trim() : null,
      fixed_json: fixedJson,
      status: '1',
    };
    try {
      await postApiCall(API_APIWEBHOOK_CREATE, body);
      toast.success('API created successfully!');
      navigate(`/${ROUTES.APIWEBHOOK.LIST}`);
    } catch (err: any) {
      toast.error(friendlyApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ScrollPage>
      {alertMessage && (
        <div
          className="api-webhook-marquee mb-4 overflow-hidden rounded-md border border-border/60 bg-muted/40 px-4 py-2 text-sm text-foreground"
          role="status"
        >
          <div
            className="api-webhook-marquee__track"
            style={{ animationDuration: `${String(alertMessage).length * 0.2 + 15}s` }}
          >
            <span className="api-webhook-marquee__item">{alertMessage}</span>
            <span className="api-webhook-marquee__item" aria-hidden="true">
              {alertMessage}
            </span>
          </div>
          <style>{`
            .api-webhook-marquee__track {
              display: flex;
              width: max-content;
              white-space: nowrap;
              animation-name: apiWebhookMarquee;
              animation-timing-function: linear;
              animation-iteration-count: infinite;
            }
            .api-webhook-marquee:hover .api-webhook-marquee__track {
              animation-play-state: paused;
            }
            .api-webhook-marquee__item {
              padding-right: 3rem;
            }
            @keyframes apiWebhookMarquee {
              from { transform: translateX(0); }
              to { transform: translateX(-50%); }
            }
          `}</style>
        </div>
      )}

      <PageHeader
        title="Add API Webhook"
        description="Configure an outbound HTTP endpoint. Use the API builder to test it before saving."
        actions={
          <Button asChild variant="outline">
            <Link to={`/${ROUTES.APIWEBHOOK.LIST}`}>
              <ArrowLeft className="h-4 w-4" />
              Back
            </Link>
          </Button>
        }
      />

      <form className="space-y-4" onSubmit={handleSubmit} noValidate>
        {/* Basics */}
        <Card>
          <CardHeader>
            <CardTitle>Basics</CardTitle>
            <CardDescription>Name your webhook and pick the HTTP method.</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="webhook_name">
                Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="webhook_name"
                required
                type="text"
                placeholder="e.g. OrderStatusCallback"
                value={webhook_name}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^[A-Za-z]*$/.test(value)) setWebhookName(value);
                }}
                autoComplete="off"
                title="Only letters allowed."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="request_type">
                Method <span className="text-destructive">*</span>
              </Label>
              <Select value={request_type} onValueChange={setRequestType}>
                <SelectTrigger id="request_type">
                  <SelectValue placeholder="Select request type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="GET">GET</SelectItem>
                  <SelectItem value="POST">POST</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Request builder */}
        {request_type && (
          <Card>
            <CardHeader>
              <CardTitle>Request builder</CardTitle>
              <CardDescription>
                Paste a cURL or configure URL, headers, and body. Click <b>Test</b> to preview
                the response.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ApiBuilder
                initialUrl={url}
                initialMethod={apiMethod}
                initialRawBody={['POST'].includes(request_type) ? requestBodySnapshot : ''}
                onUseUrl={handleUseUrl}
                onMethod={(m: any) => {
                  const next = String(m || '').toUpperCase() === 'GET' ? 'GET' : 'POST';
                  setRequestType(next);
                }}
                onResponse={handleBuilderResponse}
                onStateChange={handleApiBuilderChange}
                onClear={handleBuilderClear}
              />
            </CardContent>
          </Card>
        )}

        {/* Request Parameters */}
        {request_type && (
          <Card>
            <CardHeader>
              <CardTitle>Request parameters</CardTitle>
              <CardDescription>
                Fixed parameters merged into the outbound request. Values are editable per row.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div className="space-y-2">
                <Label>Add parameters</Label>
                <div ref={paramDropdownRef} className="relative">
                  <div
                    onClick={() =>
                      requestParamOptions.length > 0 && setParamDropdownOpen((v) => !v)
                    }
                    className={cn(
                      'flex min-h-[40px] flex-wrap items-center gap-1 rounded-md border border-border/60 px-3 py-1.5 pr-9 text-sm shadow-[inset_2px_2px_5px_rgba(163,177,198,0.35),inset_-2px_-2px_5px_rgba(255,255,255,0.9)]',
                      requestParamOptions.length === 0
                        ? 'cursor-not-allowed bg-muted/50 text-muted-foreground'
                        : 'cursor-pointer bg-card',
                    )}
                  >
                    {selectedRequestParams.length === 0 ? (
                      <span className="text-muted-foreground">
                        {requestParamOptions.length === 0
                          ? 'No parameters available'
                          : 'Select request parameters'}
                      </span>
                    ) : (
                      selectedRequestParams.map((id) => {
                        const opt = requestParamOptions.find((o) => o.value === id);
                        return opt ? (
                          <span
                            key={id}
                            className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-xs"
                          >
                            {opt.label}
                            <button
                              type="button"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                toggleRequestParam(id);
                              }}
                              className="text-muted-foreground hover:text-foreground"
                              aria-label={`Remove ${opt.label}`}
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ) : null;
                      })
                    )}
                    <ChevronDown
                      className={cn(
                        'pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground transition-transform',
                        paramDropdownOpen && 'rotate-180',
                      )}
                    />
                  </div>
                  {paramDropdownOpen && (
                    <div className="absolute left-0 right-0 top-[calc(100%+4px)] z-30 max-h-56 overflow-y-auto rounded-md border border-border bg-popover shadow-lg">
                      {requestParamOptions.map((opt) => {
                        const checked = selectedRequestParams.includes(opt.value);
                        return (
                          <div
                            key={opt.value}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              toggleRequestParam(opt.value);
                            }}
                            className={cn(
                              'flex cursor-pointer items-center gap-2 border-b border-border/50 px-3 py-2 text-sm',
                              checked ? 'bg-primary/10' : 'hover:bg-muted',
                            )}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              readOnly
                              className="h-4 w-4 accent-primary"
                            />
                            <span>{opt.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {selectedRequestParams.length > 0 && (
                <div className="space-y-2">
                  <Label>Values</Label>
                  <div className="overflow-x-auto rounded-md border border-border">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/40">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium">Parameter</th>
                          <th className="px-3 py-2 text-left font-medium">Value</th>
                          <th className="w-16 px-3 py-2 text-right font-medium">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedRequestParams.map((id) => {
                          const opt = requestParamOptions.find((o) => o.value === id);
                          if (!opt) return null;
                          const currentValue = paramCustomValues[id] ?? opt.parameter_value;
                          const isEditing = editingParamId === id;
                          return (
                            <tr key={id} className="border-t border-border">
                              <td className="px-3 py-2 font-medium">{opt.parameter_name}</td>
                              <td className="px-3 py-2">
                                {isEditing ? (
                                  <Input
                                    value={editingParamValue}
                                    onChange={(e) => setEditingParamValue(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') saveEditParam();
                                      if (e.key === 'Escape') cancelEditParam();
                                    }}
                                    autoFocus
                                    className="h-8"
                                  />
                                ) : (
                                  <span
                                    className={cn(
                                      'font-mono text-xs',
                                      paramCustomValues[id] !== undefined
                                        ? 'text-primary'
                                        : 'text-muted-foreground',
                                    )}
                                  >
                                    {currentValue}
                                  </span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-right">
                                {isEditing ? (
                                  <span className="inline-flex items-center gap-1">
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 text-emerald-600 hover:text-emerald-700"
                                      onClick={saveEditParam}
                                      aria-label="Save"
                                    >
                                      <Check className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 text-destructive hover:text-destructive"
                                      onClick={cancelEditParam}
                                      aria-label="Cancel"
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </span>
                                ) : (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8"
                                    onClick={() => startEditParam(id)}
                                    aria-label="Edit value"
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {fixedJson && (
                <div className="space-y-2 lg:col-span-2">
                  <Label>Fixed JSON preview</Label>
                  <pre className="max-h-48 overflow-auto rounded-md border border-border bg-muted/40 p-3 font-mono text-xs">
                    {JSON.stringify(JSON.parse(fixedJson), null, 2)}
                  </pre>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Body / Headers / Response params */}
        {(request_type === 'GET' || request_type === 'POST') && (
          <Card>
            <CardHeader>
              <CardTitle>
                {request_type === 'GET' ? 'Body text' : 'Payload & response'}
              </CardTitle>
              <CardDescription>
                {request_type === 'GET'
                  ? 'Enter a body-text template used for this GET webhook.'
                  : 'The POST payload sent when the webhook fires. Test to inspect the response and pick parameters to save.'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_360px]">
                <div className="overflow-hidden rounded-md border border-border">
                  {/* Tab bar */}
                  <div className="flex items-center gap-2 border-b border-border bg-muted/40 px-3 py-2">
                    <button
                      type="button"
                      className={cn(
                        'inline-flex items-center gap-1 rounded-md px-3 py-1 text-sm',
                        bodyTab === 'body'
                          ? 'bg-background font-medium text-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground',
                      )}
                      onClick={() => setBodyTab('body')}
                    >
                      {request_type === 'GET' ? 'Body text' : 'Payload'}
                      {request_type === 'GET' && (
                        <span className="text-destructive">*</span>
                      )}
                    </button>
                    <button
                      type="button"
                      className={cn(
                        'inline-flex items-center gap-1 rounded-md px-3 py-1 text-sm',
                        bodyTab === 'headers'
                          ? 'bg-background font-medium text-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground',
                      )}
                      onClick={() => setBodyTab('headers')}
                    >
                      Headers
                      {Object.keys(responseHeaders).length > 0 && (
                        <span className="rounded-full bg-primary/15 px-1.5 text-xs text-primary">
                          {Object.keys(responseHeaders).length}
                        </span>
                      )}
                    </button>
                    <div className="ml-auto flex items-center gap-2">
                      {bodyTab === 'body' && (
                        <>
                          <div className="inline-flex rounded-md border border-border p-0.5">
                            <button
                              type="button"
                              className={cn(
                                'px-2 py-0.5 text-xs rounded',
                                bodyViewMode === 'raw'
                                  ? 'bg-muted font-medium'
                                  : 'text-muted-foreground',
                              )}
                              onClick={() => handleViewModeChange('raw')}
                            >
                              Raw
                            </button>
                            <button
                              type="button"
                              className={cn(
                                'px-2 py-0.5 text-xs rounded',
                                bodyViewMode === 'pretty'
                                  ? 'bg-muted font-medium'
                                  : 'text-muted-foreground',
                              )}
                              onClick={() => handleViewModeChange('pretty')}
                            >
                              Pretty
                            </button>
                          </div>
                          <span className="rounded-md bg-muted px-2 py-0.5 text-xs font-medium">
                            JSON
                          </span>
                          <CopyButton
                            text={request_type === 'GET' ? body_text : payload}
                          />
                        </>
                      )}
                    </div>
                  </div>

                  {/* Body panel */}
                  {bodyTab === 'body' && (
                    <div className="flex">
                      {bodyViewMode === 'raw' && (
                        <div className="w-10 select-none border-r border-border bg-muted/30 py-3 text-right font-mono text-xs leading-6 text-muted-foreground">
                          {getLineCount(request_type === 'GET' ? body_text : payload).map((n) => (
                            <div key={n} className="px-2">
                              {n}
                            </div>
                          ))}
                        </div>
                      )}
                      {bodyViewMode === 'pretty' ? (
                        <pre
                          className="max-h-96 min-h-[10rem] flex-1 overflow-auto bg-muted/40 p-3 font-mono text-xs text-foreground"
                          dangerouslySetInnerHTML={{
                            __html: (() => {
                              const raw =
                                request_type === 'GET' || responseStatus ? body_text : payload;
                              try {
                                return syntaxHighlight(JSON.parse(raw));
                              } catch {
                                return raw
                                  .replace(/&/g, '&amp;')
                                  .replace(/</g, '&lt;')
                                  .replace(/>/g, '&gt;');
                              }
                            })(),
                          }}
                        />
                      ) : request_type === 'GET' ? (
                        <Textarea
                          name="body_text"
                          value={body_text}
                          className="min-h-40 flex-1 !border-0 !bg-muted/40 !shadow-none font-mono text-xs text-foreground focus-visible:ring-0"
                          onChange={(e) => {
                            const value = e.target.value;
                            if (/^\s+$/.test(value)) return;
                            setBodyText(value);
                          }}
                          onBlur={(e) => setBodyText(e.target.value.trim())}
                          spellCheck={false}
                        />
                      ) : (
                        <Textarea
                          required
                          name="payload"
                          value={payload}
                          className="min-h-40 flex-1 !border-0 !bg-muted/40 !shadow-none font-mono text-xs text-foreground focus-visible:ring-0"
                          onChange={(e) => setPayload(e.target.value)}
                          spellCheck={false}
                        />
                      )}
                    </div>
                  )}

                  {/* Headers panel */}
                  {bodyTab === 'headers' && (
                    <div className="p-3">
                      {Object.keys(responseHeaders).length === 0 ? (
                        <p className="py-8 text-center text-sm text-muted-foreground">
                          No response headers yet. Click <strong>Test</strong> to send a request.
                        </p>
                      ) : (
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-border">
                              <th className="pb-2 text-left font-medium">Header</th>
                              <th className="pb-2 text-left font-medium">Value</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(responseHeaders).map(([k, v]: any) => (
                              <tr key={k} className="border-b border-border/50">
                                <td className="py-2 pr-3 font-mono text-xs">{k}</td>
                                <td className="break-all py-2 font-mono text-xs">{v}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}

                  {/* Status pill */}
                  {responseStatus && (
                    <div className="flex items-center gap-2 border-t border-border bg-muted/20 px-3 py-2 text-xs">
                      <span className="text-muted-foreground">Status:</span>
                      <span
                        className={cn(
                          'inline-flex items-center rounded-full border px-2 py-0.5 font-medium',
                          statusToneClass(responseStatus.status),
                        )}
                      >
                        {responseStatus.status} {responseStatus.statusText}
                      </span>
                    </div>
                  )}
                </div>

                {/* Response parameters picker */}
                {responseParams.length > 0 && (
                  <div className="flex flex-col overflow-hidden rounded-md border border-border">
                    <div className="flex items-center justify-between gap-2 border-b border-border bg-muted/40 px-3 py-2">
                      <div>
                        <div className="text-sm font-medium">Response parameters</div>
                        <div className="text-xs text-muted-foreground">Select to save</div>
                      </div>
                      <div className="flex gap-2 text-xs">
                        <button
                          type="button"
                          className="text-primary hover:underline"
                          onClick={handleSelectAll}
                        >
                          All
                        </button>
                        <span className="text-border">|</span>
                        <button
                          type="button"
                          className="text-primary hover:underline"
                          onClick={handleDeselectAll}
                        >
                          None
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 border-b border-border bg-background px-3 py-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                      <span className="w-4" />
                      <span className="flex-1">Parameter</span>
                      <span className="flex-1">Sample</span>
                    </div>
                    <div className="max-h-96 flex-1 overflow-y-auto">
                      {(() => {
                        const segments: any[] = [];
                        let curGroup: any = null;
                        responseParams.forEach((p: any) => {
                          if (p.groupKey) {
                            if (!curGroup || curGroup.groupKey !== p.groupKey) {
                              curGroup = { type: 'group', groupKey: p.groupKey, items: [p] };
                              segments.push(curGroup);
                            } else curGroup.items.push(p);
                          } else {
                            curGroup = null;
                            segments.push({ type: 'flat', item: p });
                          }
                        });
                        return segments.map((seg: any) =>
                          seg.type === 'group' ? (
                            <div key={seg.groupKey}>
                              <div className="border-b border-border/50 bg-muted/20 px-3 py-1 text-xs font-medium text-muted-foreground">
                                {seg.groupKey}
                              </div>
                              {seg.items.map(({ key, displayKey, sampleValue }: any) => (
                                <label
                                  key={key}
                                  className={cn(
                                    'flex cursor-pointer items-center gap-2 border-b border-border/50 px-3 py-1.5 text-xs',
                                    selectedParams.has(key)
                                      ? 'bg-primary/5'
                                      : 'hover:bg-muted/40',
                                  )}
                                >
                                  <input
                                    type="checkbox"
                                    className="h-4 w-4 accent-primary"
                                    checked={selectedParams.has(key)}
                                    onChange={() => handleParamToggle(key)}
                                  />
                                  <span className="flex-1 font-mono">{displayKey}</span>
                                  <span className="flex-1 truncate font-mono text-muted-foreground">
                                    {sampleValue}
                                  </span>
                                </label>
                              ))}
                            </div>
                          ) : (
                            <label
                              key={seg.item.key}
                              className={cn(
                                'flex cursor-pointer items-center gap-2 border-b border-border/50 px-3 py-1.5 text-xs',
                                selectedParams.has(seg.item.key)
                                  ? 'bg-primary/5'
                                  : 'hover:bg-muted/40',
                              )}
                            >
                              <input
                                type="checkbox"
                                className="h-4 w-4 accent-primary"
                                checked={selectedParams.has(seg.item.key)}
                                onChange={() => handleParamToggle(seg.item.key)}
                              />
                              <span className="flex-1 font-mono">
                                {seg.item.displayKey || seg.item.key}
                              </span>
                              <span className="flex-1 truncate font-mono text-muted-foreground">
                                {seg.item.sampleValue}
                              </span>
                            </label>
                          ),
                        );
                      })()}
                    </div>
                    <div className="flex items-center justify-between border-t border-border bg-muted/40 px-3 py-2 text-xs">
                      <span className="text-muted-foreground">
                        <span className="mr-1 rounded-full bg-primary/15 px-1.5 text-primary">
                          {selectedParams.size}
                        </span>
                        of {responseParams.length} selected
                      </span>
                      <Button
                        type="button"
                        size="sm"
                        disabled={selectedParams.size === 0}
                        onClick={handleSaveParams}
                      >
                        Save parameters
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {parameterJson && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Response parameter JSON</Label>
                    <CopyButton text={parameterJson} />
                  </div>
                  <pre className="max-h-48 overflow-auto rounded-md border border-border bg-muted/40 p-3 font-mono text-xs">
                    {parameterJson}
                  </pre>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <div className="flex items-center justify-end gap-2">
          <Button asChild variant="ghost">
            <Link to={`/${ROUTES.APIWEBHOOK.LIST}`}>Cancel</Link>
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {saving ? 'Adding…' : 'Add API webhook'}
          </Button>
        </div>
      </form>

      <ConfirmDialog
        open={showConfirmModal}
        onOpenChange={setShowConfirmModal}
        title="Confirm submission"
        description={
          <>
            Is the{' '}
            <span className="font-medium text-foreground">
              {request_type === 'POST' ? 'payload' : 'body text'}
            </span>{' '}
            correct? The webhook will be saved and active immediately.
          </>
        }
        confirmLabel="Yes, add webhook"
        onConfirm={submitWebhook}
      />
    </ScrollPage>
  );
};

export default CreateApiWebhook;
